/**
 * Facility Holidays API Service
 *
 * CRUD operations for facility-level holidays.
 * API endpoints: /api/v2/facilities/{facilityId}/holidays
 */
import {
  schedulingFetchDelete,
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";

// Response from API (snake_case)
export interface HolidayResponse {
  id: number;
  name: string;
  date: string; // ISO date string "2026-12-25"
  facility_id: number;
}

// Request payload for create/update (snake_case for API)
export interface HolidayPayload {
  name: string;
  date: string; // ISO date string "2026-12-25"
}

/**
 * GET /api/v2/facilities/{facilityId}/holidays
 * Fetch all holidays for a facility (ordered by date)
 */
export async function getFacilityHolidays(
  facilityId: string
): Promise<HolidayResponse[]> {
  const response = await schedulingFetchGet<HolidayResponse[]>(
    `facilities/${facilityId}/holidays`
  );

  return Array.isArray(response) ? response as HolidayResponse[] : [];
}

/**
 * POST /api/v2/facilities/{facilityId}/holidays
 * Create a new holiday for a facility
 */
export async function createFacilityHoliday(
  facilityId: string,
  payload: HolidayPayload
): Promise<HolidayResponse> {
  return schedulingFetchPost<HolidayResponse>(
    `facilities/${facilityId}/holidays`,
    payload
  );
}

/**
 * PATCH /api/v2/facilities/{facilityId}/holidays/{holidayId}
 * Update an existing holiday
 */
export async function updateFacilityHoliday(
  facilityId: string,
  holidayId: number,
  payload: HolidayPayload
): Promise<HolidayResponse> {
  return schedulingFetchPatch<HolidayResponse>(
    `facilities/${facilityId}/holidays/${holidayId}`,
    payload
  );
}

/**
 * DELETE /api/v2/facilities/{facilityId}/holidays/{holidayId}
 * Delete a holiday
 */
export async function deleteFacilityHoliday(
  facilityId: string,
  holidayId: number
): Promise<void> {
  await schedulingFetchDelete(
    `facilities/${facilityId}/holidays/${holidayId}`
  );
}
